/**
 * Truncated source cell for stats table.
 * Shows Voluum source ID (left-aligned) and source name (from smswiser_country_source).
 * Fixed width for source ID so names align vertically.
 * Hover: ID, Name, Tags (StatsHoverPopover) when performance tags are present.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';
import { truncateEnd } from '../../../shared';

interface TruncatedSourceCellProps {
  /** Voluum source ID (e.g. 22, 7867) */
  sourceId?: string | null;
  /** Source display name (from smswiser_country_source.name) */
  label: string;
  /** Performance tags from source-performance-tags API */
  performanceTags?: string[];
  /** Tag + date range for hover tooltip */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}

export const TruncatedSourceCell: FC<TruncatedSourceCellProps> = ({
  sourceId,
  label,
  performanceTags,
  performanceTagDetails,
  tagColors,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayLabel = label || sourceId || '—';
  const truncatedLabel = truncateEnd(displayLabel, 32);

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!sourceId) return;
    navigator.clipboard
      .writeText(sourceId)
      .then(() => toastSuccess('Source ID copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyLabel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = truncatedLabel.full || displayLabel;
    if (!text || text === '—') return;
    navigator.clipboard
      .writeText(text)
      .then(() => toastSuccess('Source name copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const hasTags =
    ((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0;

  const tagsLine = (performanceTags?.length ?? 0) > 0 ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex ml-1"
      compact
      disableTooltip
    />
  ) : null;

  const cell = (
    <div className="flex min-w-0 items-baseline gap-1.5 text-xs text-muted-foreground">
      <span
        className="inline-flex shrink-0 w-[8ch] font-mono tabular-nums"
        aria-hidden
      >
        <button
          type="button"
          onClick={handleCopyId}
          title="Click to copy source ID"
          className="font-medium text-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary disabled:pointer-events-none disabled:opacity-50"
          aria-label="Copy source ID"
          disabled={!sourceId}
        >
          {sourceId || '—'}
        </button>
      </span>
      <button
        type="button"
        onClick={handleCopyLabel}
        title="Click to copy source name"
        className="min-w-0 truncate text-left text-inherit bg-transparent border-0 p-0 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary font-medium text-foreground/80"
        aria-label="Copy source name"
      >
        {truncatedLabel.display}
      </button>
      {tagsLine}
    </div>
  );

  if (!hasTags) return cell;

  const hoverContent: ReactNode = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="ID" valueClassName="font-mono">
        {sourceId || '—'}
      </StatsTooltipSection>
      <StatsTooltipSection label="Name" valueClassName="font-medium">
        {truncatedLabel.full || displayLabel || '—'}
      </StatsTooltipSection>
      <div>
        <StatsTooltipTagsBlock
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
        />
      </div>
    </div>
  );

  return <StatsHoverPopover content={hoverContent}>{cell}</StatsHoverPopover>;
};
